import { useEffect, useRef } from 'react';
import { useRowAlertContext } from './RowAlertProvider.js';

const ALL_INDEX_NEWS_TICKERS = [
  { code: 'NIFTY',      label: 'NIFTY 50' },
  { code: 'SENSEX',     label: 'SENSEX' },
  { code: 'NIFTYBANK',  label: 'NIFTY BANK' },
  { code: 'NIFTYIT',    label: 'NIFTY IT' },
  { code: 'NIFTYMETAL', label: 'NIFTY METAL' },
];

// The API sends published_at as "YYYY-MM-DD HH:MM:SS" (space-separated, no
// timezone) — not valid ISO 8601, so new Date() on it directly is
// implementation-defined (works in V8, unreliable elsewhere). Swap the space
// for a "T" so every browser parses it the same, consistent way.
const parsePublishedAt = (value) => (value ? new Date(value.replace(' ', 'T')) : null);

const POLL_INTERVAL_MS = 20 * 1000;
const ACCENT_COLOR = '#7c3aed'; // violet — distinct from the shared toast's default green
const MAX_TOASTS = 3; // beyond this, collapse the rest into a single "+N more" toast

// Deliberately distinct from useNewRowAlerts.js's chime (2-tone sine, 880Hz→1174.66Hz)
// so index news is audibly different from Intraday Base / Result Analysis alerts.
const playIndexNewsChime = () => {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioContextClass();
    const playTone = (frequency, startTime, duration) => {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = 'triangle';
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, startTime);
      gain.gain.exponentialRampToValueAtTime(0.25, startTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    const now = ctx.currentTime;
    playTone(1046.50, now, 0.12);        // C6
    playTone(1318.51, now + 0.1, 0.12);  // E6
    playTone(1567.98, now + 0.2, 0.22);  // G6
    setTimeout(() => ctx.close(), 700);
  } catch (err) {
    console.error('Unable to play index news chime:', err);
  }
};

// Mounted globally in App.js (inside RowAlertProvider, outside the routed pages) so a new
// NIFTY/SENSEX/sectoral-index headline pops a toast no matter which page the user is on —
// same treatment as IntradayBaseAlert / ResultAnalysisAlert, just its own chime/color/source.
const IndexNewsAlert = () => {
  const { pushToasts } = useRowAlertContext();
  const seenLinksRef = useRef(null); // null until the first poll establishes a baseline

  useEffect(() => {
    const fetchData = async () => {
      try {
        const apiUrl = 'https://live1.finstrats.com';
        const dateParam = new Date().toISOString().split('T')[0];

        const entries = await Promise.all(ALL_INDEX_NEWS_TICKERS.map(async ({ code, label }) => {
          try {
            const res = await fetch(`${apiUrl}/getNewsSentiment?ticker=${encodeURIComponent(code)}&date=${dateParam}`);
            if (!res.ok) return [];
            const data = await res.json();
            return (data.articles || [])
              .filter(a => !a.date || a.date === dateParam)
              .map(a => ({ label, headline: a.headline, link: a.url, publishedAt: a.published_at }));
          } catch {
            return [];
          }
        }));

        // Each ticker's own array already comes back newest-first from the API, but
        // concatenating five separately-sorted arrays isn't globally sorted — re-sort
        // so the toasts below (capped at MAX_TOASTS) surface the truly latest headlines,
        // not just whichever ticker happened to be flattened in first.
        const articles = entries.flat().sort((a, b) => {
          if (!a.publishedAt) return 1;
          if (!b.publishedAt) return -1;
          return parsePublishedAt(b.publishedAt) - parsePublishedAt(a.publishedAt);
        });

        if (seenLinksRef.current === null) {
          seenLinksRef.current = new Set(articles.map(a => a.link));
          return;
        }

        const freshArticles = articles.filter(a => !seenLinksRef.current.has(a.link));
        if (freshArticles.length === 0) return;

        freshArticles.forEach(a => seenLinksRef.current.add(a.link));
        playIndexNewsChime();

        const shown = freshArticles.slice(0, MAX_TOASTS);
        const remaining = freshArticles.length - shown.length;
        const toasts = shown.map(a => {
          const timeLabel = a.publishedAt
            ? parsePublishedAt(a.publishedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
            : null;
          return {
            id: `index-news-${a.link}-${Date.now()}-${Math.random()}`,
            title: timeLabel ? `${a.label} · ${timeLabel}` : a.label,
            detail: a.headline,
            linkTo: '/index',
            accentColor: ACCENT_COLOR,
          };
        });
        if (remaining > 0) {
          toasts.push({
            id: `index-news-more-${Date.now()}-${Math.random()}`,
            title: 'Index News',
            detail: `+${remaining} more`,
            linkTo: '/index',
            accentColor: ACCENT_COLOR,
          });
        }
        pushToasts(toasts, 10 * 1000);
      } catch (err) {
        console.error('Error polling index news:', err);
      }
    };

    fetchData();
    const intervalId = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default IndexNewsAlert;
